import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckIcon,
  CreditCardIcon,
  XMarkIcon,
  LockClosedIcon,
} from '@heroicons/react/24/solid';
import { CheckoutGateway } from '../Types/PaymentType';

/**
 * The payment step, asked for after the customer commits to a plan.
 *
 * <p>This is the shape Hostinger and Naukri both use: the pricing page stays a pricing page, and
 * payment method is a separate, deliberate step. Putting the gateway picker on the plan page made the
 * customer choose a provider before they had chosen a plan, which is a decision they had not yet
 * earned the context to make - and it left the most important number on the page sitting in a panel
 * that looked like a receipt rather than an invitation.
 *
 * <p>The order summary is repeated here on purpose. Between picking a plan and confirming a payment
 * something can change the figure - a coupon, a tax rate, a plan going out of stock - and the amount
 * being confirmed has to be the amount the customer was just shown, not one they remember.
 */

interface Props {
  open: boolean;
  onClose: () => void;

  planName: string;
  planCredits?: number;
  subtotal: number;
  total: number;

  /** Rendered as itemised tax lines under the subtotal. Empty when no tax applies. */
  taxLines: { label: string; amount: number }[];

  gateways: CheckoutGateway[];
  selectedGateway: CheckoutGateway | null;
  onSelectGateway: (gateway: CheckoutGateway) => void;

  /** Shown only for the gateways that reject an order without one. */
  needsPhone: boolean;
  customerPhone: string;
  onCustomerPhoneChange: (value: string) => void;

  processing: boolean;
  onConfirm: () => void;
}

/**
 * Providers that send the browser to their own site to take the payment.
 *
 * <p>Not a cosmetic distinction. Payment research is unambiguous that a generic "Pay now" button makes
 * people hesitate when the next step is a redirect they were not told about, and the drop-off is
 * worse than for in-page methods - the customer has committed money and then appears to have their
 * order abandoned. So the button below says where they are going, and the redirect is stated in
 * words as well as implied by the label.
 */
const REDIRECT_GATEWAYS: ReadonlySet<string> = new Set(['CASHFREE', 'PAYU']);

const inr = (value: number) =>
  `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const PaymentCheckoutModal: React.FC<Props> = ({
  open,
  onClose,
  planName,
  planCredits,
  subtotal,
  total,
  taxLines,
  gateways,
  selectedGateway,
  onSelectGateway,
  needsPhone,
  customerPhone,
  onCustomerPhoneChange,
  processing,
  onConfirm,
}) => {
  // Escape closes, but never mid-payment: a half-raised dialog while a gateway SDK is opening is
  // unrecoverable, because the modal is what the customer returns to.
  useEffect(() => {
    if (!open || processing) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, processing, onClose]);

  // Stop the page behind the dialog scrolling under the customer's thumb on mobile.
  useEffect(() => {
    if (!open) {
      return;
    }
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  const redirects = Boolean(selectedGateway && REDIRECT_GATEWAYS.has(selectedGateway.gateway));
  const hasTax = taxLines.length > 0;

  const confirmLabel = processing
    ? 'Processing…'
    : redirects && selectedGateway
      ? `Continue to ${selectedGateway.displayName}`
      : `Pay ${inr(total)}`;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="checkout-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 z-[90] flex items-end justify-center bg-slate-950/80 p-0 backdrop-blur-sm sm:items-center sm:p-4"
          onClick={processing ? undefined : onClose}
          role="presentation"
        >
          <motion.div
            key="checkout-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkout-title"
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            onClick={(event) => event.stopPropagation()}
            // Bottom sheet on a phone, centred dialog from `sm` up. The grid keeps the footer pinned
            // to the bottom of the card while the body scrolls, so the pay button is always reachable
            // by thumb even when the gateway list is tall.
            className="flex max-h-[92vh] w-full max-w-md flex-col overflow-hidden rounded-t-3xl border border-white/10 bg-slate-900 shadow-2xl shadow-black/60 sm:max-h-[88vh] sm:rounded-3xl"
          >
            <div className="flex items-start justify-between gap-4 border-b border-white/10 p-5">
              <div className="min-w-0">
                <p className="text-[10px] font-bold uppercase tracking-widest text-indigo-400">
                  Complete your payment
                </p>
                <h2 id="checkout-title" className="mt-1 truncate text-lg font-bold text-white">
                  {planName}
                  {planCredits ? (
                    <span className="ml-2 text-sm font-semibold text-slate-400">
                      {planCredits} credits
                    </span>
                  ) : null}
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                disabled={processing}
                aria-label="Close"
                className="rounded-xl p-1.5 text-slate-400 transition-colors hover:bg-white/5 hover:text-white disabled:opacity-40"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              <div className="space-y-2 rounded-2xl border border-white/10 bg-slate-950/60 p-4">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Plan price</span>
                  <span className="font-mono text-slate-200">{inr(subtotal)}</span>
                </div>
                {taxLines.map((line) => (
                  <div key={line.label} className="flex justify-between text-sm">
                    <span className="text-slate-400">{line.label}</span>
                    <span className="font-mono text-slate-200">{inr(line.amount)}</span>
                  </div>
                ))}
                <div className="flex items-baseline justify-between gap-3 border-t border-white/10 pt-3">
                  <span className="text-sm font-bold text-white">Amount payable</span>
                  <span className="font-mono text-xl font-black text-indigo-300">{inr(total)}</span>
                </div>
                <p className="text-right text-[11px] text-slate-500">
                  {hasTax ? 'Inclusive of GST' : 'No GST applies to this plan'}
                </p>
              </div>

              <fieldset className="mt-5" disabled={processing}>
                <legend className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                  Payment method
                </legend>

                <div className="mt-3 space-y-2">
                  {gateways.length === 0 && (
                    <p className="rounded-xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-xs font-semibold text-amber-300">
                      No payment method is currently enabled. Please contact your company admin.
                    </p>
                  )}

                  {gateways.map((gateway) => {
                    const isSelected = selectedGateway?.gateway === gateway.gateway;
                    const brand = gateway.brandColor || '#4F46E5';
                    const leaves = REDIRECT_GATEWAYS.has(gateway.gateway);

                    return (
                      <label
                        key={gateway.gateway}
                        className={`flex cursor-pointer items-center gap-3 rounded-2xl border p-3.5 transition-all ${
                          isSelected
                            ? 'bg-white/[0.06]'
                            : 'border-white/10 hover:border-white/20 hover:bg-white/[0.03]'
                        } ${processing ? 'cursor-not-allowed opacity-60' : ''}`}
                        style={isSelected ? { borderColor: brand } : undefined}
                      >
                        <input
                          type="radio"
                          name="checkout-gateway"
                          value={gateway.gateway}
                          checked={isSelected}
                          onChange={() => onSelectGateway(gateway)}
                          className="sr-only"
                        />

                        <span
                          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-sm font-black text-white"
                          style={{ backgroundColor: brand }}
                          aria-hidden="true"
                        >
                          {gateway.displayName.charAt(0)}
                        </span>

                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-bold text-white">
                            {gateway.displayName}
                          </span>
                          {gateway.description && (
                            <span className="block truncate text-[11px] text-slate-400">
                              {gateway.description}
                            </span>
                          )}
                        </span>

                        {isSelected ? (
                          <CheckIcon className="h-5 w-5 flex-shrink-0" style={{ color: brand }} />
                        ) : (
                          <span
                            className="h-5 w-5 flex-shrink-0 rounded-full border-2 border-slate-600"
                            aria-hidden="true"
                          />
                        )}
                      </label>
                    );
                  })}
                </div>
              </fieldset>

              {/* Cashfree rejects an order without customer_phone and no recruiter phone is stored
                  server-side, so it has to be collected here - but only once that gateway is chosen,
                  so the form does not ask for something four out of five customers will not need. */}
              {needsPhone && (
                <div className="mt-5">
                  <label
                    htmlFor="checkout-phone"
                    className="text-[10px] font-bold uppercase tracking-widest text-slate-400"
                  >
                    Phone number
                  </label>
                  <input
                    id="checkout-phone"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    disabled={processing}
                    value={customerPhone}
                    onChange={(event) =>
                      onCustomerPhoneChange(event.target.value.replace(/[^\d+\s-]/g, ''))
                    }
                    placeholder="e.g. 98765 43210"
                    className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-800/60 px-4 py-3 text-sm text-white placeholder-slate-500 focus:border-indigo-500 focus:outline-none disabled:opacity-60"
                  />
                  <p className="mt-1.5 text-[11px] text-slate-500">
                    Used by {selectedGateway?.displayName || 'the gateway'} to confirm this payment. Not
                    stored on your account.
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-2.5 border-t border-white/10 bg-slate-900/80 p-5">
              {/* Stated in words as well as implied by the button label, because the two together are
                  what removes the hesitation - a label change alone reads as a small styling detail. */}
              <p className="flex items-center justify-center gap-1.5 text-center text-[11px] text-slate-500">
                {redirects ? (
                  <>
                    <CreditCardIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    You will be taken to {selectedGateway?.displayName} to finish. We return you here
                    afterwards.
                  </>
                ) : (
                  <>
                    <LockClosedIcon className="h-3.5 w-3.5 flex-shrink-0" />
                    Secured by {selectedGateway?.displayName || 'the gateway'}. Credits are added only
                    after the payment is verified.
                  </>
                )}
              </p>

              <button
                type="button"
                onClick={onConfirm}
                disabled={processing || gateways.length === 0}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-3.5 font-black text-sm uppercase tracking-tight text-white shadow-lg shadow-indigo-500/25 transition-all hover:from-indigo-400 hover:to-purple-500 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <CreditCardIcon className="h-5 w-5 flex-shrink-0" />
                <span className="text-center">{confirmLabel}</span>
              </button>

              <button
                type="button"
                onClick={onClose}
                disabled={processing}
                className="w-full rounded-2xl px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-slate-400 transition-colors hover:text-slate-200 disabled:opacity-40"
              >
                Cancel
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PaymentCheckoutModal;